import { useEffect, useState } from "react";
import {
  CreditCard, Loader2, RefreshCw, Trash2, Check, AlertTriangle, Copy, ExternalLink, Webhook,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

const Row = ({ ok, children, testid }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={testid}>
    {ok ? (
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0f8a5f]" />
    ) : (
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6b00]" />
    )}
    <span className={ok ? "text-[#2d2824]" : "font-bold text-[#8a4300]"}>{children}</span>
  </div>
);

export const StripeAdminCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const origin = window.location.origin;
  const wantedUrl = `${origin}/api/stripe/webhook`;

  const load = () => adminApi.stripe().then(setData).catch(() => toast.error("Could not read Stripe"));

  useEffect(() => {
    load();
  }, []);

  const register = async () => {
    setBusy("register");
    try {
      const r = await adminApi.registerWebhook(origin);
      toast.success(`Webhook registered for ${r.registered}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register the webhook");
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this webhook endpoint from Stripe?")) return;
    setBusy(id);
    try {
      await adminApi.deleteWebhook(id);
      toast.success("Endpoint deleted");
      load();
    } catch (e) {
      toast.error("Could not delete");
    } finally {
      setBusy("");
    }
  };

  const sync = async () => {
    setBusy("sync");
    try {
      const r = await adminApi.syncCatalog();
      toast.success(r.results.join(" · "));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sync the catalog");
    } finally {
      setBusy("");
    }
  };

  if (!data) {
    return (
      <div className="rounded-2xl border border-[#e5e0d8] bg-white p-5">
        <Loader2 className="h-5 w-5 animate-spin text-[#2d2824]" />
      </div>
    );
  }

  const mine = data.endpoints.find((e) => e.url === wantedUrl);
  const priceProblems = data.prices.filter((p) => !p.ok);

  return (
    <div className="rounded-2xl border border-[#e5e0d8] bg-white p-5" data-testid="stripe-admin">
      <div className="mb-3 flex items-center gap-2 font-bold text-[#2d2824]">
        <CreditCard className="h-5 w-5" /> Stripe setup
        <button
          onClick={load}
          className="ml-auto rounded-full border border-[#e5e0d8] p-1.5 text-[#6e655b] hover:bg-[#f3efea]"
          data-testid="stripe-reload"
          aria-label="reload"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {data.error && (
        <p className="mb-2 rounded-xl bg-[#fff1f3] p-2 text-xs font-bold text-[#e11d48]">
          {data.error}
        </p>
      )}

      <div className="space-y-1.5">
        <Row ok testid="stripe-mode">
          Mode <b>{data.mode}</b> · account <b>{data.account_id || "—"}</b>
          {data.account.country ? ` · ${data.account.country}` : ""}
        </Row>
        <Row ok={!!data.account.details_submitted} testid="stripe-kyc">
          {data.account.details_submitted
            ? "Account details submitted"
            : "Onboarding not finished — claim the account and complete KYC before going live"}
        </Row>
        <Row ok={!!data.account.charges_enabled} testid="stripe-charges">
          {data.account.charges_enabled ? "Live charges enabled" : "Charges not enabled yet (test mode only)"}
        </Row>
      </div>

      {/* webhook for THIS domain */}
      <div className="mt-4 rounded-xl bg-[#f7f3ec] p-3">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
          <Webhook className="h-4 w-4" /> Webhook for this domain
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all text-xs text-[#2d2824]" data-testid="stripe-wanted-url">
            {wantedUrl}
          </code>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(wantedUrl);
              toast.success("URL copied");
            }}
            className="rounded-full border border-[#e5e0d8] bg-white p-1.5 text-[#6e655b] hover:bg-white"
            data-testid="stripe-copy-url"
            aria-label="copy url"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-2">
          {mine && mine.events >= data.required_events.length ? (
            <Row ok testid="stripe-webhook-ok">
              Registered and listening to {mine.events} events ({mine.status})
            </Row>
          ) : (
            <Row ok={false} testid="stripe-webhook-missing">
              {mine
                ? `This endpoint only listens to ${mine.events} of ${data.required_events.length} events — re-register it`
                : "No Stripe webhook points at this domain — subscription renewals and cancellations will not sync"}
            </Row>
          )}
        </div>
        <button
          onClick={register}
          disabled={busy === "register"}
          className="mt-2 flex items-center gap-2 rounded-full bg-[#2d2824] px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
          data-testid="stripe-register-webhook"
        >
          {busy === "register" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Webhook className="h-4 w-4" />}
          {mine ? "Re-register this domain" : "Register webhook for this domain"}
        </button>
        <p className="mt-1 text-xs text-[#6e655b]">
          Run this from the domain you actually want Stripe to call (the deployed site, or your own
          domain later). Checkout return pages need no configuration — they follow the browser.
        </p>
      </div>

      {/* all endpoints */}
      <div className="mt-3">
        <div className="mb-1 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
          Registered endpoints ({data.endpoints.length})
        </div>
        <div className="space-y-1" data-testid="stripe-endpoints">
          {data.endpoints.length === 0 && (
            <p className="text-sm text-[#6e655b]">None yet.</p>
          )}
          {data.endpoints.map((e) => (
            <div key={e.id} className="flex items-center gap-2 rounded-xl border border-[#f3efea] px-2 py-1.5">
              <span className="flex-1 break-all text-xs text-[#2d2824]">
                {e.url}
                <span className="ml-1 text-[#a89e92]">
                  · {e.events} events · {e.status}
                  {e.url === wantedUrl ? " · this domain" : ""}
                </span>
              </span>
              <button
                onClick={() => remove(e.id)}
                disabled={busy === e.id}
                className="rounded-full border border-[#e11d48]/40 p-1.5 text-[#e11d48] hover:bg-[#fff1f3] disabled:opacity-60"
                data-testid={`stripe-delete-${e.id}`}
                aria-label="delete endpoint"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* prices */}
      <div className="mt-3">
        <div className="mb-1 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
          Products & prices
        </div>
        <table className="w-full text-left text-xs" data-testid="stripe-prices">
          <tbody>
            {data.prices.map((p) => (
              <tr key={p.lookup_key} className="border-t border-[#f3efea]">
                <td className="py-1 pr-2 font-bold text-[#2d2824]">{p.lookup_key}</td>
                <td className="pr-2">
                  {p.live_amount === null ? "missing" : `€${p.live_amount.toFixed(2)}`}
                  {p.recurring ? ` / ${p.recurring}` : ""}
                </td>
                <td>{p.ok ? <Check className="h-3.5 w-3.5 text-[#0f8a5f]" /> : "⚠︎"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {priceProblems.length > 0 && (
          <p className="mt-1 text-xs font-bold text-[#8a4300]">
            {priceProblems.length} price(s) missing or out of date — sync the catalog.
          </p>
        )}
        <button
          onClick={sync}
          disabled={busy === "sync"}
          className="mt-2 flex items-center gap-2 rounded-full border-2 border-[#2d2824] px-4 py-2 text-sm font-bold text-[#2d2824] disabled:opacity-60"
          data-testid="stripe-sync-catalog"
        >
          {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Sync catalog
        </button>
      </div>

      <a
        href="https://dashboard.stripe.com/test/webhooks"
        target="_blank"
        rel="noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#8a2be2]"
      >
        Open Stripe webhooks dashboard <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  );
};
